import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import { useNotificationStore } from "@/stores/notification-store";
import type { Dispatch, SetStateAction } from "react";
import { uploadImage } from "@/api/uploads";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";
import { libErrorsMessages } from "@/i18n/messages/lib-errors";
type AttachmentSetter = Dispatch<SetStateAction<ImageAttachment[]>>;
const localUrls = new WeakMap<AttachmentSetter, Set<string>>();

/** Release local files when their owning draft resets or unmounts, including
 * files whose uploads have not completed yet. Progress updates keep live URLs. */
export function releaseComposerAttachmentUrls(
  setAttachments: AttachmentSetter,
  keep?: Set<string>,
): void {
  const urls = localUrls.get(setAttachments);
  if (!urls) return;
  for (const url of urls)
    if (!keep?.has(url)) {
      URL.revokeObjectURL(url);
      urls.delete(url);
    }
}

function releaseLocalUrl(setAttachments: AttachmentSetter, url: string) {
  URL.revokeObjectURL(url);
  localUrls.get(setAttachments)?.delete(url);
}

export function attachmentIsImage(attachment: ImageAttachment): boolean {
  if (!attachment.contentType) return true;
  return /^image\/(png|jpeg|gif|webp|bmp)$/i.test(attachment.contentType);
}

export function attachmentIsVideo(attachment: ImageAttachment): boolean {
  return (
    attachment.contentType === "video/mp4" ||
    /\.mp4$/i.test(attachment.filename ?? "")
  );
}

export function attachmentFormat(attachment: ImageAttachment): string {
  const extension = attachment.filename?.split(".").pop();
  if (extension && attachment.filename?.includes("."))
    return extension.toUpperCase();
  return (
    attachment.contentType?.split("/").pop()?.toUpperCase() ||
    translate(libChatMessages, "imageFormat")
  );
}

export async function attachComposerFile(
  file: File,
  setAttachments: Dispatch<SetStateAction<ImageAttachment[]>>,
): Promise<ImageAttachment | null> {
  const id = crypto.randomUUID();
  const url = URL.createObjectURL(file);
  const urls = localUrls.get(setAttachments) ?? new Set<string>();
  urls.add(url);
  localUrls.set(setAttachments, urls);
  const isVideo = file.type === "video/mp4" || /\.mp4$/i.test(file.name);
  const contentType = isVideo
    ? "video/mp4"
    : file.type || "application/octet-stream";
  let retainLocalVideo = isVideo;
  setAttachments((current) => [
    ...current,
    {
      id,
      url,
      uploading: true,
      filename: file.name,
      contentType,
    },
  ]);
  try {
    const uploadFile =
      file.type === contentType
        ? file
        : new File([file], file.name, { type: contentType });
    const uploaded = await (isVideo
      ? uploadImage(uploadFile, (uploadProgress) =>
          setAttachments((current) =>
            current.map((item) =>
              item.id === id ? { ...item, uploadProgress } : item,
            ),
          ),
        )
      : uploadImage(uploadFile));
    const attachment: ImageAttachment = {
      id,
      url: uploaded.url,
      imageId: uploaded.id,
      uploading: false,
      filename: uploaded.original_filename || file.name,
      contentType: uploaded.content_type,
      width: uploaded.width,
      height: uploaded.height,
    };
    setAttachments((current) =>
      current.map((item) => (item.id === id ? attachment : item)),
    );
    retainLocalVideo = false;
    return attachment;
  } catch (error) {
    setAttachments((current) => {
      if (isVideo && !current.some((item) => item.id === id))
        releaseLocalUrl(setAttachments, url);
      return isVideo
        ? current.map((item) =>
            item.id === id
              ? {
                  ...item,
                  uploading: false,
                  uploadError:
                    error instanceof Error
                      ? error.message
                      : translate(libErrorsMessages, "uploadFailed"),
                }
              : item,
          )
        : current.filter((item) => item.id !== id);
    });
    useNotificationStore.getState().addSystemToast({
      id: `attachment-error-${id}`,
      title: translate(libChatMessages, "attachFailed"),
      level: "error",
      description:
        error instanceof Error
          ? error.message
          : translate(libChatMessages, "tryAgain"),
    });
    return null;
  } finally {
    // A failed video remains playable until the user removes it.
    if (!retainLocalVideo) releaseLocalUrl(setAttachments, url);
  }
}

export function removeComposerAttachment(
  id: string,
  setAttachments: Dispatch<SetStateAction<ImageAttachment[]>>,
) {
  setAttachments((current) => {
    const attachment = current.find((item) => item.id === id);
    if (attachment?.url.startsWith("blob:"))
      releaseLocalUrl(setAttachments, attachment.url);
    return current.filter((item) => item.id !== id);
  });
}
